/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { isAndroid, isIOS } from "react-device-detect";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  BiChevronDown,
  BiChevronUp,
  BiSolidBot,
  BiSolidChat,
  BiSolidImageAdd,
  BiSolidMessageDetail,
  BiSolidTrash,
  BiSolidVideoPlus,
  BiSolidVolumeFull,
  BiX
} from "react-icons/bi";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import PopoverWithAudio, {
  IPopoverWithAudioType
} from "../../components/common/PopoverWithAudio";
import ProBadge from "../../components/ui/ProBadge";
import { AlertsType } from "../../constants/alerts.constant";
import usePermission from "../../hooks/usePermission";
import Utilities from "../../utils";
import AppearanceCustomPoweredBy from "./AppearanceCustomPoweredBy";
import AppearanceMessageNotificationComponent from "./AppearanceMessageNotification.component";
import AppearanceMessageSound from "./AppearanceMessageSound.component";
import AppearanceNotificationComponent from "./AppearanceNotification.component";
import {
  handleResetAppearance,
  handleUpdateAppearanceItem,
  selectAppearanceState
} from "./appearanceSlice";
import ColorDropDown from "./ColorDropDown";
import { IAiAppearance, IVideoScenarioAppearance } from "./types";

export const BiLouise = () => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M26.6836 7.74844H17.9565V3.57422L5.27734 16.2622V24.2907H13.9973V28.4631L22.4276 20.0329H26.6836V7.74844ZM9.53334 20.0329V12.0044H22.4276V20.0329H9.53334Z"
        fill="white"
      />
    </svg>
  );
};

const AppearanceSharedAi = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useAppDispatch();

  const permissions = usePermission();
  const { configs, originalData } = useAppSelector(selectAppearanceState);

  const avatarRef = useRef<HTMLInputElement | null>(null);

  const iconImageRef = useRef<HTMLInputElement | null>(null);
  const iconVideoRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    setValue,
    watch,
    formState: { errors },
    control
  } = useFormContext<IAiAppearance>();

  // ========================= Avatar ============================ //
  const [avatarsByUser, setAvatarsByUser] = useState<
    string | ArrayBuffer | null
  >();

  const handleSelectCustomAvatar = () => {
    if (avatarRef.current) {
      avatarRef.current.value = "";
      avatarRef.current.click();
    }
  };

  const handleVerifyAndSelectCustomAvatar = (e: any) => {
    if (e.target.files && e.target.files[0]) {
      const file: File = e.target.files[0];

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return false;
      }

      if (file.size / (1024 * 1024) > 5) {
        toast.error(t(AlertsType.ISSTKO008, { size: 5 }));
        return false;
      }

      if (
        !["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(
          file.type
        )
      ) {
        toast.error(t(AlertsType.ISSTKO009));
        return false;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setAvatarsByUser(reader.result);
        handleUpdateItem("logo", file);
      };
      reader.onerror = (error) => console.log(error);
    }
  };

  const handleRemoveClientAvatar = () => {
    setAvatarsByUser(null);
    handleUpdateItem("logo", "");
  };

  // ========================= General ============================ //
  const handleUpdateItem = (key: keyof IAiAppearance, value: any) => {
    if (
      key === "icon" &&
      value.type === "image" &&
      value.value &&
      typeof value.value === "object"
    ) {
      const file: File = value.value;

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return;
      }

      if (file.size / (1024 * 1024) > 5) {
        toast.error(t(AlertsType.ISSTKO008, { size: 5 }));
        return;
      }

      if (
        !["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(
          file.type
        )
      ) {
        toast.error(t(AlertsType.ISSTKO009));
        return;
      }
    }

    if (
      key === "icon" &&
      value.type === "video" &&
      value.value &&
      typeof value.value === "object"
    ) {
      const file: File = value.value;

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return;
      }

      if (file.size / (1024 * 1024) > 30) {
        toast.error(t(AlertsType.ISSTKO008, { size: 30 }));
        return;
      }

      if (file?.type) {
        if (!Utilities.isSupportedVideoFormatMimeType(file.type)) {
          toast.error(t(AlertsType.ISSTKO009));
          return;
        }
      } else if (file?.name) {
        if (!Utilities.isSupportedVideoFormatWithFileName(file.type)) {
          toast.error(t(AlertsType.ISSTKO009));
          return;
        }
      }
    }

    setValue(key, value);
    dispatch(handleUpdateAppearanceItem({ key, value }));
  };

  // Popover voice and texts
  const voiceCommandPopData: IPopoverWithAudioType = {
    audioUrl: Utilities.VoiceCommandAudioLinks[i18n.language],
    contentText: t("DashboardSettingCustomizeChat/pop-07")
  };

  const voiceResponsePopData: IPopoverWithAudioType = {
    audioUrl: Utilities.VoiceResponseAudioLinks[i18n.language],
    contentText: t("DashboardSettingCustomizeChat/pop-10")
  };

  const SaveConversationData: IPopoverWithAudioType = {
    audioUrl: Utilities.VoiceTrack[i18n.language],
    contentText: t("DashboardSettingCustomizeChat/pop-08")
  };

  const SaveStatsData: IPopoverWithAudioType = {
    audioUrl: Utilities.VoiceSaveStatsTrack[i18n.language],
    contentText: t("DashboardSettingCustomizeChat/pop-09")
  };

  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(
    null
  );

  const handleVoiceResponseSound = () => {
    const language = i18n.language;
    const gender = watch("assistantGender");

    if (language && gender) {
      const url = Utilities.VoiceResponseSound.find(
        (dr) => dr.language === language && dr.gender === gender
      )?.cdnPath;
      if (url) {
        const audio = new Audio(url);

        if (currentAudio) {
          currentAudio.pause();
          currentAudio.currentTime = 0;
        }

        setCurrentAudio(audio);
        audio.play();
      }
    }
  };

  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    };
  }, [currentAudio]);

  useEffect(() => {
    if (
      configs?.logo &&
      configs.logo !==
        "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png" &&
      configs.logo !==
        "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png" &&
      typeof configs.logo !== "object"
    ) {
      setAvatarsByUser(configs.logo);
      handleUpdateItem("logo", configs?.logo);
    }
  }, [configs?.logo]);

  return (
    <div className="appearance-shared-ai tw-w-full">
      <React.Fragment>
        {/* Avatar */}
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          {/* label */}
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t("DashboardSettingCustomizeChat/t-04")}
            {!permissions.settingsPage.widget.customization.avatar && (
              <ProBadge className="tw-ms-2" />
            )}
          </div>
          {/* content */}
          <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full tw-flex-wrap">
            <button
              type="button"
              disabled={!permissions.settingsPage.widget.customization.avatar}
              onClick={() =>
                handleUpdateItem(
                  "logo",
                  "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
                )
              }
              className={`tw-p-0 tw-bg-transparent tw-w-10 tw-h-10 tw-cursor-pointer tw-rounded-full tw-border-4 ${
                (configs as IAiAppearance).logo ===
                "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
                  ? "tw-border-blue-600"
                  : "tw-border-gray-200"
              } tw-border-solid`}
            >
              <img
                className="tw-w-full tw-h-full tw-rounded-full tw-object-cover"
                src="https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
                alt=""
              />
            </button>

            <button
              type="button"
              disabled={!permissions.settingsPage.widget.customization.avatar}
              onClick={() =>
                handleUpdateItem(
                  "logo",
                  "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png"
                )
              }
              className={`tw-p-0 tw-bg-transparent tw-w-10 tw-h-10 tw-cursor-pointer tw-rounded-full tw-border-4 ${
                (configs as IAiAppearance).logo ===
                "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png"
                  ? "tw-border-blue-600"
                  : "tw-border-gray-200"
              } tw-border-solid`}
            >
              <img
                className="tw-w-full tw-h-full tw-rounded-full tw-object-cover"
                src="https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png"
                alt=""
              />
            </button>

            {avatarsByUser && (
              <div
                className={`tw-w-10 tw-h-10 tw-cursor-pointer tw-rounded-full tw-border-4 tw-relative ${
                  typeof (configs as IAiAppearance).logo === "object" ||
                  ((configs as IAiAppearance).logo !==
                    "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png" &&
                    (configs as IAiAppearance).logo !==
                      "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png")
                    ? "tw-border-blue-600"
                    : "tw-border-gray-200"
                } tw-border-solid`}
              >
                <BiX
                  size={22}
                  className="tw-text-gray-100 tw-absolute tw-right-[-5px] tw-top-[-10px] tw-cursor-pointer tw-bg-black tw-rounded-full tw-z-10"
                  onClick={handleRemoveClientAvatar}
                />
                <button
                  type="button"
                  className="tw-p-0 tw-bg-transparent tw-border-0 tw-rounded-full tw-w-full tw-relative"
                  disabled={
                    !permissions.settingsPage.widget.customization.avatar
                  }
                  onClick={() => {
                    if (
                      avatarRef.current &&
                      avatarRef.current.files &&
                      avatarRef.current.files.length > 0
                    )
                      handleUpdateItem("logo", avatarRef.current?.files[0]);
                    else handleUpdateItem("logo", avatarsByUser);
                  }}
                >
                  <img
                    className="tw-absolute tw-w-full tw-h-9 tw-top-[-20px] tw-left-0 tw-right-0 tw-bottom-0 tw-rounded-full tw-object-cover"
                    src={avatarsByUser.toString()}
                    alt=""
                  />
                </button>
              </div>
            )}

            {/* Image uploader */}
            <button
              type="button"
              className="tw-pl-3 tw-pr-4 tw-py-2 tw-bg-white tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-gap-2 tw-flex tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"
              onClick={handleSelectCustomAvatar}
              disabled={!permissions.settingsPage.widget.customization.avatar}
            >
              <BiSolidImageAdd size={22} className="tw-text-gray-400 tw-mr-2" />
              {t("DashboardSettingCustomizeChat/pop-05")}
            </button>

            <input
              type="file"
              hidden
              ref={avatarRef}
              accept="image/png, image/jpeg, image/jpg, image/gif"
              onChange={handleVerifyAndSelectCustomAvatar}
            />
          </div>
          {/* Validation */}
          {errors.logo && (
            <span className="tw-text-red-500 tw-text-sm tw-block">
              {errors.logo?.message}
            </span>
          )}
        </div>
        <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

        {/* Theme */}
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t("DashboardSettingCustomizeChat/l-06")}
          </div>
          <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full">
            <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-w-full">
              <div className="tw-w-full tw-col-span-12">
                <Form.Group>
                  <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("DashboardSettingCustomizeChat/l-12")}
                    {!permissions.settingsPage.widget.customization.theme && (
                      <ProBadge
                        className="tw-ms-2"
                        onClose={() => {
                          dispatch(handleResetAppearance());
                        }}
                      />
                    )}
                  </Form.Label>
                  <Form.Select
                    disabled={
                      !permissions.settingsPage.widget.customization.theme
                    }
                    isInvalid={!!errors.theme}
                    {...register("theme", {
                      onChange(event) {
                        dispatch(
                          handleUpdateAppearanceItem({
                            key: "theme",
                            value: event.target.value
                          })
                        );
                      }
                    })}
                  >
                    <option value={"light"}>
                      {t("DashboardSettingCustomizeChat/l-05")}
                    </option>
                    <option value={"dark"}>
                      {t("DashboardSettingCustomizeChat/l-07")}
                    </option>
                  </Form.Select>
                  {/* Validation */}
                  {errors.theme && (
                    <span className="tw-text-red-500 tw-text-sm tw-block">
                      {errors.theme?.message}
                    </span>
                  )}
                </Form.Group>
              </div>
              <div className="tw-w-full tw-col-span-12">
                <Form.Group>
                  <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("DashboardSettingCustomizeChat/t-01")}
                    {!permissions.settingsPage.widget.customization.colors && (
                      <ProBadge
                        className="tw-ms-2"
                        onClose={() => {
                          dispatch(handleResetAppearance());
                        }}
                      />
                    )}
                  </Form.Label>
                  <Controller
                    control={control}
                    name="color"
                    render={({ field: { onChange, value } }) => (
                      <ColorDropDown
                        acceptableColors={[
                          { label: "Grey", value: "#6B7280" },
                          { label: "Red", value: "#EF4444" },
                          { label: "Orange", value: "#F97316" },
                          { label: "Yellow", value: "#F59E0B" },
                          { label: "Green", value: "#10B981" },
                          { label: "Teal", value: "#14B8A6" },
                          { label: "Cyan", value: "#06B6D4" },
                          { label: "Light Blue", value: "#0EA5E9" },
                          { label: "Blue", value: "#3B82F6" },
                          { label: "Indigo", value: "#6366F1" },
                          { label: "Purple (Violet)", value: "#8B5CF6" },
                          { label: "Pink", value: "#EC4899" }
                        ]}
                        disabled={
                          !permissions.settingsPage.widget.customization.colors
                        }
                        isInvalid={!!errors.color}
                        errorMessage={errors.color?.message}
                        onColorChanged={(color) => {
                          onChange(color);

                          dispatch(
                            handleUpdateAppearanceItem({
                              key: "color",
                              value: color
                            })
                          );
                        }}
                        currentColor={value}
                      />
                    )}
                  />
                </Form.Group>
              </div>
              <div className="tw-w-full tw-col-span-12">
                <Form.Group>
                  <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("DashboardSettingCustomizeChat/t-02")}
                    {!permissions.settingsPage.widget.customization
                      .position && <ProBadge className="tw-ms-2" />}
                  </Form.Label>
                  <Form.Select
                    disabled={
                      !permissions.settingsPage.widget.customization.position
                    }
                    isInvalid={!!errors.position}
                    {...register("position", {
                      onChange(event) {
                        dispatch(
                          handleUpdateAppearanceItem({
                            key: "position",
                            value: event.target.value
                          })
                        );
                      }
                    })}
                  >
                    <option value={"right"}>
                      {t("DashboardSettingCustomizeChat/l-09")}
                    </option>
                    <option value={"left"}>
                      {t("DashboardSettingCustomizeChat/l-08")}
                    </option>
                  </Form.Select>
                  {/* Validation */}
                  {errors.position && (
                    <span className="tw-text-red-500 tw-text-sm tw-block">
                      {errors.position?.message}
                    </span>
                  )}
                </Form.Group>
              </div>
            </div>
          </div>
        </div>
        <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

        {/* Floating button */}
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t("DashboardSettingCustomizeChat/floating-btn")}
          </div>
          <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full">
            <Form.Group className="tw-mb-4">
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t("DashboardSettingCustomizeChat/t-03")}
                {!permissions.settingsPage.widget.customization.icon && (
                  <ProBadge
                    className="tw-ms-2"
                    onClose={() => {
                      dispatch(handleResetAppearance());
                    }}
                  />
                )}
              </Form.Label>
              <div className="tw-flex tw-items-center tw-gap-5 tw-flex-wrap">
                {[
                  { id: 4, ui: <BiLouise /> },
                  { id: 1, ui: <BiSolidBot size={25} /> },
                  { id: 2, ui: <BiSolidChat size={25} /> },
                  { id: 3, ui: <BiSolidMessageDetail size={25} /> }
                ].map((item) => (
                  <button
                    type="button"
                    disabled={
                      !permissions.settingsPage.widget.customization.icon
                    }
                    key={item.id}
                    className={`tw-border-0 tw-w-10 tw-h-10 ${
                      (configs as IAiAppearance).icon.type === "icon" &&
                      item.id === Number((configs as IAiAppearance).icon.value)
                        ? "tw-bg-blue-500"
                        : "tw-bg-gray-400"
                    } tw-text-white tw-rounded-[999px] tw-flex tw-items-center tw-flex-col tw-justify-center`}
                    onClick={() =>
                      handleUpdateItem("icon", {
                        type: "icon",
                        value: item.id.toString()
                      })
                    }
                  >
                    {item.ui}
                  </button>
                ))}

                {/* Image manager */}
                <button
                  type="button"
                  disabled={!permissions.settingsPage.widget.customization.icon}
                  className={`tw-pl-3 tw-pr-4 tw-py-2  ${
                    (configs as IAiAppearance).icon.type === "image"
                      ? "tw-bg-blue-500 tw-text-white"
                      : "tw-bg-white tw-text-gray-400"
                  } tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-gap-2 tw-flex tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal`}
                  onClick={() =>
                    handleUpdateItem("icon", {
                      type: "image",
                      value:
                        (originalData as IAiAppearance).icon.type === "image" &&
                        (originalData as IAiAppearance).icon.value
                          ? (originalData as IAiAppearance).icon.value
                          : ""
                    })
                  }
                >
                  <BiSolidImageAdd size={22} className=" tw-mr-2" />
                  Image
                </button>

                {/* Video manage */}
                <button
                  type="button"
                  disabled={!permissions.settingsPage.widget.customization.icon}
                  className={`tw-pl-3 tw-pr-4 tw-py-2 ${
                    (configs as IAiAppearance).icon.type === "video"
                      ? "tw-bg-blue-500 tw-text-white"
                      : "tw-bg-white tw-text-gray-400"
                  } tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-gap-2 tw-flex tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal`}
                  onClick={() =>
                    handleUpdateItem("icon", {
                      type: "video",
                      value:
                        (originalData as IAiAppearance).icon.type === "video" &&
                        (originalData as IAiAppearance).icon.value
                          ? (originalData as IAiAppearance).icon.value
                          : ""
                    })
                  }
                >
                  <BiSolidVideoPlus size={22} className="tw-mr-2" />
                  Video
                </button>
              </div>
            </Form.Group>
          </div>

          {/* Image manager */}
          {(configs as IAiAppearance).icon.type === "image" && (
            <Form.Group className="lg:tw-w-[70%] tw-w-full tw-mb-4">
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t("DashboardSettingCustomizeChat/upload-image")}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/gif"
                  ref={iconImageRef}
                  isInvalid={
                    !!iconImageRef.current?.files &&
                    !(configs as IAiAppearance).icon.value
                  }
                  onChange={(e: any) =>
                    handleUpdateItem("icon", {
                      type: "image",
                      value: e.target.files[0]
                    })
                  }
                />
                <InputGroup.Text>
                  <BiSolidTrash
                    size={22}
                    className="tw-text-gray-400"
                    onClick={() => {
                      if (iconImageRef.current) {
                        iconImageRef.current.value = "";

                        handleUpdateItem("icon", {
                          type: "image",
                          value: ""
                        });
                      }
                    }}
                  />
                </InputGroup.Text>
              </InputGroup>

              {(configs as IAiAppearance).icon.value &&
                typeof (configs as IAiAppearance).icon.value !== "object" && (
                  <div className="tw-mt-6">
                    <span className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mb-3">
                      Current image:
                    </span>{" "}
                    <a
                      href={(configs as IAiAppearance).icon.value}
                      target="_blank"
                      rel="noreferrer"
                      className="tw-text-sm tw-mt-6 tw-text-blue-400 tw-underline"
                    >
                      Link
                    </a>
                  </div>
                )}
            </Form.Group>
          )}

          {/* Video manage */}
          {(configs as IAiAppearance).icon.type === "video" && (
            <Form.Group className="lg:tw-w-[70%] tw-w-full tw-mb-4">
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t("DashboardSettingCustomizeChat/upload-video")}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="file"
                  accept={
                    isIOS
                      ? "video/mp4, video/mov, video/m4v, video/x-matroska, video/avi"
                      : isAndroid
                      ? "video/mp4, video/3gp, video/webm, video/x-matroska, video/avi"
                      : "video/mp4, video/webm, video/ogg"
                  }
                  ref={iconVideoRef}
                  isInvalid={
                    !!iconVideoRef.current?.files &&
                    !(configs as IAiAppearance).icon.value
                  }
                  onChange={(e: any) =>
                    handleUpdateItem("icon", {
                      type: "video",
                      value: e.target.files[0]
                    })
                  }
                />
                <InputGroup.Text>
                  <BiSolidTrash
                    size={22}
                    className="tw-text-gray-400"
                    onClick={() => {
                      if (iconVideoRef.current) {
                        iconVideoRef.current.value = "";

                        handleUpdateItem("icon", {
                          type: "video",
                          value: ""
                        });
                      }
                    }}
                  />
                </InputGroup.Text>
              </InputGroup>
              {(configs as IAiAppearance).icon.value &&
                typeof (configs as IAiAppearance).icon.value !== "object" && (
                  <div className="tw-mt-6">
                    <span className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mb-3">
                      Current video:
                    </span>{" "}
                    <a
                      href={(configs as IAiAppearance).icon.value}
                      target="_blank"
                      rel="noreferrer"
                      className="tw-text-sm tw-text-blue-400 tw-underline"
                    >
                      Link
                    </a>
                  </div>
                )}
            </Form.Group>
          )}

          {/* <Form.Group className="tw-mb-4">
            <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              Message
            </Form.Label>
            <Form.Control
              placeholder="Hello"
              {...register("notificationMessage", {
                onChange: (e) => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: "notificationMessage",
                      value: e.target.value
                    })
                  );
                }
              })}
            />
          </Form.Group> */}

          {/* Video shape */}
          {(configs as IAiAppearance).icon.type === "video" && (
            <Form.Group className="tw-mb-4">
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t("DashboardSettingCustomizeChat/button-shape")}
                {!permissions.settingsPage.widget.customization
                  .roundedVideo && <ProBadge className="tw-ms-2" />}
              </Form.Label>
              <div className="tw-flex tw-items-center tw-gap-6">
                <button
                  type="button"
                  onClick={() => handleUpdateItem("roundedVideo", false)}
                  className={`${
                    !(configs as IVideoScenarioAppearance).roundedVideo
                      ? "tw-border-4 tw-border-solid tw-border-blue-500"
                      : "tw-border-4 tw-border-solid tw-border-transparent"
                  } tw-w-9 tw-h-10 tw-bg-gray-300 tw-rounded ${
                    (configs as IVideoScenarioAppearance).icon.type !== "video"
                      ? "tw-cursor-not-allowed"
                      : ""
                  }`}
                />
                <button
                  type="button"
                  onClick={() => handleUpdateItem("roundedVideo", true)}
                  className={`${
                    (configs as IVideoScenarioAppearance).roundedVideo
                      ? "tw-border-4 tw-border-solid tw-border-blue-500"
                      : "tw-border-4 tw-border-solid tw-border-transparent"
                  } tw-w-10 tw-h-10 tw-bg-gray-300 tw-rounded-full`}
                />
              </div>
            </Form.Group>
          )}

          {/* Validation */}
          {errors.icon && (
            <span className="tw-text-red-500 tw-text-sm tw-block">
              {errors.icon?.message}
            </span>
          )}
        </div>

        <AppearanceNotificationComponent
          register={register}
          watch={watch}
          className="tw-mt-6"
        />

        <AppearanceMessageNotificationComponent
          register={register}
          watch={watch}
          errors={errors}
          control={control}
        />

        <AppearanceMessageSound
          register={register}
          watch={watch}
          errors={errors}
          control={control}
        />

        <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

        {/* Voice Messages */}
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t("DashboardSettingCustomizeChat/l-16")}
          </div>
          <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
            <Form.Group className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
              <Form.Check
                disabled={
                  !permissions.settingsPage.widget.customization.voiceCommand
                }
                type="switch"
                id="isVoiceActive"
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t("DashboardSettingCustomizeChat/allow-send-voice")}
                isInvalid={!!errors.isVoiceActive}
                feedbackType="invalid"
                feedback={errors.isVoiceActive?.message}
                {...register("isVoiceActive", {
                  onChange: (e) => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: "isVoiceActive",
                        value: e.target.checked
                      })
                    );
                  }
                })}
              />
              {!permissions.settingsPage.widget.customization.voiceCommand && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
              <PopoverWithAudio
                data={{
                  contentText: voiceCommandPopData.contentText,
                  audioUrl: voiceCommandPopData.audioUrl
                }}
              />
            </Form.Group>

            <Form.Group className="tw-w-full tw-flex tw-items-center tw-gap-2">
              <Form.Check
                disabled={
                  !permissions.settingsPage.widget.customization.voiceResponse
                }
                type="switch"
                id="voiceResponse"
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t("DashboardSettingCustomizeChat/allow-receive-voice")}
                isInvalid={!!errors.voiceResponse}
                feedbackType="invalid"
                feedback={errors.voiceResponse?.message}
                {...register("voiceResponse", {
                  onChange: (e) => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: "voiceResponse",
                        value: e.target.checked
                      })
                    );
                  }
                })}
              />
              {!permissions.settingsPage.widget.customization.voiceResponse && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}

              <PopoverWithAudio
                data={{
                  contentText: voiceCommandPopData.contentText,
                  audioUrl: voiceCommandPopData.audioUrl
                }}
              />
            </Form.Group>

            <Form.Group
              className={`tw-w-full tw-flex tw-items-center tw-gap-5 ${
                watch("voiceResponse") == true ? "tw-block" : "tw-hidden"
              }`}
            >
              <DropDownWithImage
                i18n={i18n}
                select={watch("voice") ?? undefined}
                onSelect={(item) => {
                  setValue("assistantGender", item.gender);
                  setValue("voice", item.voice);

                  dispatch(
                    handleUpdateAppearanceItem({
                      key: "assistantGender",
                      value: item.gender
                    })
                  );

                  dispatch(
                    handleUpdateAppearanceItem({
                      key: "voice",
                      value: item.voice
                    })
                  );
                }}
              />
            </Form.Group>
          </div>
        </div>
        <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

        {/* Other */}
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t("DashboardSettingCustomizeChat/other")}
          </div>
          <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
            <Form.Group className="tw-flex tw-items-center tw-gap-2">
              <Form.Check
                disabled={
                  !permissions.settingsPage.widget.customization
                    .save_conversation
                }
                type="switch"
                id="save_conversation"
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t("DashboardSettingCustomizeChat/t-08")}
                isInvalid={!!errors.save_conversation}
                feedbackType="invalid"
                feedback={errors.save_conversation?.message}
                {...register("save_conversation", {
                  onChange: (e) => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: "save_conversation",
                        value: e.target.checked
                      })
                    );
                  }
                })}
              />
              {!permissions.settingsPage.widget.customization
                .save_conversation && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
              <PopoverWithAudio
                data={{
                  contentText: SaveConversationData.contentText,
                  audioUrl: SaveConversationData.audioUrl
                }}
              />
            </Form.Group>

            <AppearanceCustomPoweredBy
              hasPermission={
                permissions.settingsPage.widget.customization.poweredBy
              }
              isInvalid={!!errors.customPoweredBy}
              errors={errors.customPoweredBy}
              setFormValue={setValue}
              register={register}
              watch={watch}
            />

            <Form.Group className="tw-flex tw-items-center tw-gap-2">
              <Form.Check
                disabled={
                  !permissions.settingsPage.widget.customization.defaultOpen
                }
                type="switch"
                id="isOpen"
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t("DashboardSettingCustomizeChat/l-04")}
                isInvalid={!!errors.isOpen}
                feedbackType="invalid"
                feedback={errors.isOpen?.message}
                {...register("isOpen", {
                  onChange: (e) => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: "isOpen",
                        value: e.target.checked
                      })
                    );
                  }
                })}
              />
              {!permissions.settingsPage.widget.customization.defaultOpen && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
            </Form.Group>

            {watch("isOpen") && (
              <Form.Group className="tw-flex tw-items-center tw-gap-2">
                <Form.Check
                  disabled={
                    !permissions.settingsPage.widget.customization.isFullScreen
                  }
                  type="switch"
                  id="isFullScreen"
                  className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                  label={t("DashboardSettingCustomizeChat/l-15")}
                  isInvalid={!!errors.isFullScreen}
                  feedbackType="invalid"
                  feedback={errors.isFullScreen?.message}
                  {...register("isFullScreen", {
                    onChange: (e) => {
                      dispatch(
                        handleUpdateAppearanceItem({
                          key: "isFullScreen",
                          value: e.target.checked
                        })
                      );
                    }
                  })}
                />
                {!permissions.settingsPage.widget.customization
                  .isFullScreen && (
                  <ProBadge
                    className="tw-ms-2"
                    onClose={() => {
                      dispatch(handleResetAppearance());
                    }}
                  />
                )}
              </Form.Group>
            )}

            <Form.Group className="tw-flex tw-items-center tw-gap-2">
              <Form.Check
                type="switch"
                id="saveStats"
                disabled={
                  !permissions.settingsPage.widget.customization.saveStats
                }
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t("DashboardSettingCustomizeChat/l-17")}
                isInvalid={!!errors.saveStats}
                feedbackType="invalid"
                feedback={errors.saveStats?.message}
                {...register("saveStats", {
                  onChange: (e) => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: "saveStats",
                        value: e.target.checked
                      })
                    );
                  }
                })}
              />
              {!permissions.settingsPage.widget.customization.saveStats && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}

              <PopoverWithAudio
                data={{
                  contentText: SaveStatsData.contentText,
                  audioUrl: SaveStatsData.audioUrl
                }}
              />
            </Form.Group>
          </div>
        </div>
      </React.Fragment>
    </div>
  );
};

export default AppearanceSharedAi;

const DropDownWithImage = ({
  i18n,
  onSelect,
  select
}: {
  i18n: any;
  onSelect: (item: any) => void;
  select?: string;
}) => {
  const language = i18n.language;

  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(
    null
  );

  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const [item, setItem] = useState(
    Utilities.VoiceResponseSound.filter((dr) => dr.language == language)[0]
  );

  const handleVoiceResponseSound = () => {
    const voice = select ?? item.voice;

    if (language && voice) {
      const url = Utilities.VoiceResponseSound.find(
        (dr) => dr.language === language && dr.voice === voice
      )?.cdnPath;
      if (url) {
        const audio = new Audio(url);

        if (currentAudio) {
          currentAudio.pause();
          currentAudio.currentTime = 0;
        }

        setCurrentAudio(audio);
        audio.play();
      }
    }
  };

  useEffect(() => {
    if (select) {
      const selectedItem = Utilities.VoiceResponseSound.filter(
        (dr) => dr.language == language
      ).find((item) => item.voice === select);
      if (selectedItem) {
        setItem(selectedItem);
      }
    }
  }, [select]);

  useEffect(() => {
    onSelect(item);
  }, [item]);

  return (
    <>
      <div className="tw-relative xl:tw-w-[300px] tw-w-full">
        <div
          onClick={toggleDropdown}
          className="tw-w-full tw-h-9 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex"
        >
          <div className="tw-w[220px] tw-grow tw-shrink tw-basis-0 tw-h-9 tw-px-3 tw-py-2 tw-justify-start tw-items-center tw-gap-2 tw-flex">
            <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              {item.voice}
            </div>
          </div>
          <div className="tw-px-3 tw-py-2 tw-bg-white tw-rounded-md tw-shadow tw-justify-start tw-items-center tw-gap-1 tw-flex">
            {!isOpen && <BiChevronDown className="tw-w-5 tw-h-5 tw-relative" />}
            {isOpen && <BiChevronUp className="tw-w-5 tw-h-5 tw-relative" />}
          </div>
        </div>

        {isOpen && (
          <div
            id="dropdownUsers"
            className="tw-z-10 tw-absolute tw-top-[43px]  tw-bg-white tw-rounded-lg tw-shadow tw-w-60 tw-dark:bg-gray-700"
          >
            <ul className="tw-list-none tw-h-48 tw-px-2 tw-py-2 tw-overflow-y-auto tw-text-gray-700 tw-dark:text-gray-200">
              {Utilities.VoiceResponseSound.filter(
                (dr) => dr.language == language
              ).map((item) => (
                <li
                  key={item.voice}
                  onClick={() => {
                    setItem(item);
                    toggleDropdown();
                  }}
                >
                  <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal tw-flex tw-cursor-pointer tw-rounded-xl tw-px-1 hover:tw-bg-gray-100 tw-items-center  tw-py-2 tw-hover:bg-gray-100 tw-dark:hover:bg-gray-600 tw-gap-2 tw-dark:hover:text-white">
                    {item.gender == "male" && <MaleIcon />}
                    {item.gender == "female" && <FemaleIcon />}
                    {item.voice}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="tw-cursor-pointer" onClick={handleVoiceResponseSound}>
        <BiSolidVolumeFull className="tw-w-6 tw-h-6 tw-text-blue-500 " />
      </div>
    </>
  );
};

const FemaleIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
    >
      <mask
        id="mask0_15875_28064"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="28"
        height="28"
      >
        <rect width="28" height="28" rx="14" fill="red" />
      </mask>
      <g mask="url(#mask0_15875_28064)">
        <rect width="28" height="28" fill="red" />
        <path
          d="M1 27.9996C1.21906 27.2703 1.55568 26.5942 1.9624 25.9489C2.47481 25.136 3.13504 24.4629 3.92855 23.9084C4.49342 23.5135 5.13374 23.2934 5.7844 23.0887C6.54766 22.8487 7.33389 22.671 8.06153 22.3335C8.35909 22.1955 8.63981 22.0174 8.91363 21.8367C9.12311 21.6983 9.21617 21.4677 9.28817 21.2401C9.36629 20.9919 9.54361 20.9019 9.78641 20.8768C10.0392 20.8505 10.2923 20.8269 10.5405 20.7624C10.7224 20.7151 10.8005 20.5963 10.8377 20.435C10.8702 20.2948 10.8825 20.1527 10.8817 20.008C10.8786 19.2959 10.8794 18.5838 10.879 17.8718C10.879 17.5167 10.8315 17.4676 10.4696 17.4304C10.1092 17.3933 9.75041 17.3453 9.39234 17.2857C8.72559 17.1747 8.22007 16.8294 7.81872 16.3112C7.73792 16.2069 7.74098 16.1143 7.78196 15.9992C8.01327 15.3516 8.27752 14.7157 8.47322 14.0554C8.71678 13.2334 8.90099 12.4025 8.97145 11.5461C9.0626 10.4354 9.11277 9.31953 9.31651 8.22088C9.47774 7.35208 9.71633 6.50428 10.2636 5.77647C10.5814 5.35351 10.9801 5.01791 11.4504 4.76518C11.5105 4.73294 11.5821 4.70669 11.6492 4.70481C11.9142 4.69844 12.1283 4.59757 12.3423 4.44346C12.7502 4.14949 13.2415 4.05575 13.7313 4.02088C14.3935 3.97363 15.0556 3.99463 15.6967 4.20986C16.4481 4.46221 17.0662 4.88967 17.5446 5.51212C17.8417 5.89871 18.0907 6.31305 18.2553 6.76863C18.5031 7.45482 18.5985 8.16988 18.6555 8.89319C18.7306 9.84898 18.7325 10.8111 18.8869 11.7602C18.9872 12.3763 19.1408 12.9822 19.3376 13.5762C19.5835 14.3175 19.8335 15.0576 20.0802 15.7986C20.1116 15.8931 20.1139 16.0007 20.1602 16.0862C20.2824 16.3112 20.1575 16.4184 19.9856 16.5373C19.5674 16.826 19.1404 17.0937 18.6536 17.2636C18.2186 17.4154 17.772 17.4544 17.3167 17.4304C17.1742 17.4229 17.1179 17.4567 17.111 17.6123C17.0815 18.2666 17.0321 18.9206 17.0498 19.5752C17.0605 19.9791 17.0892 20.3833 17.2604 20.765C17.322 20.9026 17.4036 20.9566 17.5495 20.9529C17.8302 20.9457 18.109 20.9709 18.3848 21.0267C18.5046 21.0511 18.5782 21.1036 18.5835 21.236C18.5966 21.5629 18.7754 21.7069 19.1028 21.7988C19.5283 21.918 19.9235 22.137 20.3368 22.3016C21.0273 22.5769 21.7243 22.8371 22.414 23.1146C23.3013 23.4715 24.1561 23.8911 24.891 24.5057C25.4187 24.947 25.8385 25.4813 26.1828 26.0704C26.5385 26.679 26.8043 27.3258 27 28H1V27.9996Z"
          fill="#FEFEFE"
        />
      </g>
    </svg>
  );
};

const MaleIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
    >
      <mask
        id="mask0_15875_28036"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="28"
        height="28"
      >
        <rect width="28" height="28" rx="14" fill="blue" />
      </mask>
      <g mask="url(#mask0_15875_28036)">
        <rect width="28" height="28" fill="blue" />
        <path
          d="M0.646484 27.999C0.720228 27.2984 1.19908 26.8589 1.69996 26.4536C2.42686 25.865 3.28593 25.4901 4.12296 25.0867C5.74436 24.3045 7.4079 23.6153 9.08102 22.9499C9.18636 22.9081 9.28405 22.855 9.31374 22.7458C9.42196 22.3471 9.67096 22.0025 9.71406 21.5735C9.74662 21.2469 9.85293 20.908 10.2906 20.871C10.4429 20.8587 10.4706 20.7457 10.485 20.6176C10.5511 20.0186 10.622 19.4196 10.69 18.8206C10.7015 18.7229 10.6842 18.6289 10.6105 18.5577C9.92859 17.9027 9.70831 17.0474 9.54838 16.1646C9.50145 15.9074 9.38652 15.6795 9.25915 15.447C8.83297 14.6695 8.86074 13.8048 8.83297 12.9542C8.82914 12.846 8.88852 12.7539 8.98907 12.7435C9.28213 12.7131 9.27064 12.5147 9.2649 12.3049C9.23233 11.0016 9.30033 9.70018 9.33672 8.3978C9.35013 7.90514 9.5637 7.48462 9.85197 7.09542C10.6603 6.00473 11.798 5.41334 13.0612 5.0156C13.882 4.7574 14.7238 4.58369 15.5666 4.41757C15.8692 4.35777 16.1805 4.33973 16.4889 4.3122C16.6201 4.30081 16.7791 4.30081 16.8394 4.43276C16.9103 4.58939 16.7503 4.65109 16.6507 4.73083C16.5674 4.79822 16.4774 4.86182 16.4113 4.94346C16.2418 5.1504 16.2284 5.3336 16.487 5.50257C17.1047 5.90695 17.6898 6.35215 18.2147 6.87614C18.7989 7.45899 19.1034 8.13865 19.0948 8.96735C19.0824 10.1406 19.0948 11.3149 19.0948 12.4881C19.0948 12.6562 19.0699 12.827 19.3189 12.8527C19.4607 12.8669 19.4674 12.995 19.4606 13.1014C19.4013 14.0383 19.4654 14.9913 18.8774 15.8333C18.6926 16.0982 18.6897 16.4864 18.5949 16.8167C18.4359 17.3664 18.2223 17.8923 17.8795 18.3564C17.7827 18.4874 17.7349 18.6251 17.7339 18.7855C17.7339 19.3664 17.7339 19.9474 17.7272 20.5283C17.7253 20.7125 17.7626 20.8435 17.9705 20.9004C18.1792 20.9574 18.276 21.1501 18.2922 21.3362C18.3325 21.8079 18.5508 22.2142 18.7136 22.6433C18.7788 22.8151 18.8812 22.9224 19.045 22.9879C20.3973 23.5261 21.7649 24.0292 23.0961 24.6139C24.1735 25.0876 25.2845 25.5214 26.2671 26.1707C27.2893 26.8462 26.4761 27.9999 25.2508 27.9999H0.647434C0.64691 27.9999 0.646484 27.9995 0.646484 27.999Z"
          fill="#FEFEFE"
        />
      </g>
    </svg>
  );
};
